import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Cell, CellProps, Column, HeaderGroup, useTable } from 'react-table'

import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import RefreshIcon from '@mui/icons-material/Refresh'
import Stack from '@mui/material/Stack'

import Pagination from '@/components/ui/pagination/Pagination'
import { Chip, ChipProps, Tooltip } from '@mui/material'
import CircleIcon from '@mui/icons-material/Circle'

import { LiveDoctorStatus } from '@/hooks/useRoomDoctorStatuses'

type DoctorData = {
  id: string
  name: string
  surname: string
  status: string
  // Merged live status (HTTP snapshot + socket events); undefined = offline
  liveStatus?: LiveDoctorStatus
}

type DoctorsTableProps = {
  data: DoctorData[]
  rowsPerPageOptions?: number[]
  onRefresh?: () => void
}

const defaultRowsPerPageOptions = [5, 10, 15]

type DoctorCellProps = {
  status: string
  liveStatus?: LiveDoctorStatus
}

function DoctorCell({ status, liveStatus }: DoctorCellProps) {
  const { t } = useTranslation()

  let color: ChipProps['color'] = 'default'
  const isOnline = !!liveStatus

  // Determine availability status based on waiting room configuration and online status
  let availabilityStatus = status
  let displayStatus = availabilityStatus

  if (!isOnline) {
    // Doctor is offline
    color = 'default'
    displayStatus = 'offline'
  } else if (liveStatus === 'in_consultation') {
    // Doctor is in consultation
    color = 'warning'
    displayStatus = 'in_consultation'
  } else {
    // Doctor is online and available/unavailable based on waiting room config
    switch (availabilityStatus) {
      case 'occupied':
        color = 'secondary'
        break
      case 'available':
        color = 'success'
        break
      case 'unavailable':
        color = 'error'
        break
      default:
        break
    }
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
      <Tooltip title={isOnline ? t('common:status.online') : t('common:status.offline')}>
        <CircleIcon
          sx={{
            fontSize: '12px',
            color: isOnline ? (liveStatus === 'in_consultation' ? 'orange' : 'green') : 'grey',
          }}
        />
      </Tooltip>
      <Chip
        color={color}
        label={
          displayStatus === 'offline'
            ? t('common:model.staff.waitingRoom.status.offline.label')
            : displayStatus === 'in_consultation'
            ? t('common:model.staff.waitingRoom.status.inConsultation.label')
            : t(`common:model.staff.waitingRoom.status.${displayStatus}.label`)
        }
        size="small"
        variant="filled"
        sx={{ fontSize: '12px' }}
      />
    </Box>
  )
}

export function DoctorsTable(props: DoctorsTableProps) {
  const { data, rowsPerPageOptions = defaultRowsPerPageOptions, onRefresh } = props
  const { t } = useTranslation()

  const [page, setPage] = useState<number>(1)
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0])

  const columns: Column<DoctorData>[] = useMemo(
    () => [
      {
        Header: t('page.waitingRoom.doctorsTable.header.name'),
        accessor: 'name',
      },
      {
        Header: t('page.waitingRoom.doctorsTable.header.surname'),
        accessor: 'surname',
      },
      {
        Header: t('page.waitingRoom.doctorsTable.header.status'),
        accessor: 'status',
        Cell: ({ row }: CellProps<DoctorData, string>) => (
          <DoctorCell status={row.original.status} liveStatus={row.original.liveStatus} />
        ),
        align: 'center',
      },
    ],
    [t]
  )

  const { getTableBodyProps, headerGroups, prepareRow, rows } = useTable<DoctorData>({
    columns,
    data,
  })

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = Math.max(0, page * rowsPerPage - rows.length)

  const handleChangePage = (newPage: number) => {
    if (page === newPage) return
    setPage(newPage)
  }

  const handleRowsPerPageChange = (rowsPerPage: number) => {
    setRowsPerPage(rowsPerPage)
    setPage(1)
  }

  return (
    <TableContainer>
      <Table sx={{ minWidth: 500 }}>
        <TableHead>
          {headerGroups.map((headerGroup: HeaderGroup<DoctorData>) => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column: HeaderGroup<DoctorData>) => {
                return (
                  <TableCell
                    {...column.getHeaderProps()}
                    align={column.align || 'left'}
                    scope="col"
                    sx={{ width: column.width, maxWidth: column.maxWidth, minWidth: column.minWidth, wordWrap: 'break-word' }}
                  >
                    {column.render('Header')}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableHead>
        <TableBody {...getTableBodyProps()}>
          {(rowsPerPage > 0 ? rows.slice((page - 1) * rowsPerPage, (page - 1) * rowsPerPage + rowsPerPage) : rows).map((row) => {
            prepareRow(row)
            return (
              <TableRow {...row.getRowProps()} key={row.original.id || row.id}>
                {row.cells.map((cell: Cell<DoctorData>) => (
                  <TableCell {...cell.getCellProps()} align={cell.column.align || 'left'}>
                    {cell.render('Cell')}
                  </TableCell>
                ))}
              </TableRow>
            )
          })}
          {emptyRows > 0 && (
            <TableRow style={{ height: 63 * emptyRows }}>
              <TableCell colSpan={6} />
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Box sx={{ display: 'flex', justifyContent: 'end', p: '10px' }}>
        <Pagination
          color="primary"
          itemsPerPageOptions={rowsPerPageOptions}
          shape="rounded"
          page={page}
          onPageChange={handleChangePage}
          onItemsPerPageChange={handleRowsPerPageChange}
          totalItems={rows.length}
        />
      </Box>
    </TableContainer>
  )
}
